/**
 * DomeOS 报警事件处理工具函数
 *
 * 提供报警事件数据转换、格式化和处理功能，包括：
 * - 字节数据格式化
 * - 百分比数据转换
 * - 时间格式化
 * - 报警事件数据结构转换
 *
 * @author Generated by GitHub Copilot
 * @date 2025-08-29
 */

import type { ApiGetDomeosOverviewAlarmEventResponse } from '@/api'
import { formatTime } from './time'

// 报警指标索引映射
const alarmIndexFilter: Record<string, string> = {
  CPUPER: 'CPU使用率',
  CPULIMITPER: 'CPU限制使用率',
  CPUREQUESTPER: 'CPU请求使用率',
  CPUUSAGE: 'CPU使用量',
  CPULOAD: 'CPU负载',
  CPULOAD1MIN: '1分钟CPU负载',
  CPULOAD5MIN: '5分钟CPU负载',
  CPULOAD15MIN: '15分钟CPU负载',
  CPUCFS: 'CPU限流百分比',
  MEMPER: '内存使用率',
  FSUSEDPER: '磁盘使用率',
  FSREAD: '磁盘读取',
  FSWRITE: '磁盘写入',
  FSUSED: '磁盘使用',
  NETRECV: '网络接收',
  NETTRANS: '网络发送',
  FSBUSY: '磁盘IO繁忙度',
  FSREADTIME: '磁盘读取时间',
  FSWRITETIME: '磁盘写入时间'
}

/**
 * 字节数据格式化函数
 * 将字节数转换为人类可读的格式（B, KiB, MiB, GiB等）
 *
 * @param bytes - 字节数
 * @returns 格式化后的字符串，如 "1.5 GiB"
 */
export const transByte = (bytes: number): string => {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return 'N/A'
  const units = [...' kMGTPEZY'].map(
    (prefix, index) => `${prefix.trim()}${index ? 'i' : ''}B`
  )
  let level = 0,
    size = bytes
  while (size >= 999.5) {
    size /= 1024
    level++
  }
  if (level >= units.length) return 'N/A'
  let value
  const unit = units[level]
  if (level === 0) value = size.toFixed(0)
  else if (size < 10) value = size.toFixed(2)
  else if (size < 100) value = size.toFixed(1)
  else if (size < 1000) value = size.toFixed(0)
  return value + unit
}

/**
 * 百分比格式化函数
 * 将小数转换为百分比字符串
 *
 * @param num - 数值（小数形式，如 0.75 表示 75%）
 * @param digits - 小数位数，默认为 0
 * @returns 格式化后的百分比字符串，如 "75%"
 */
export const transPercent = (
  num: number | null | undefined,
  digits = 0
): string => {
  if (num == null) return 'N/A'
  const numValue = Number(num)
  if (!Number.isFinite(numValue)) return 'N/A'
  return (numValue * 100).toFixed(digits) + '%'
}

/**
 * 时间格式化函数
 * 将秒数转换为人类可读的时间格式
 *
 * @param second - 秒数
 * @returns 格式化后的时间字符串，如 "1.5h", "30min", "500ms"
 */
export const transSecond = (second: number | null | undefined): string => {
  if (second == null || !Number.isFinite(Number(second))) return 'N/A'
  const secondValue = Number(second)
  if (1 <= secondValue) {
    if (secondValue < 60)
      return secondValue.toFixed(secondValue < 10 ? 1 : 0) + 's'
    if (secondValue < 3600)
      return (secondValue / 60).toFixed(secondValue < 600 ? 1 : 0) + 'min'
    if (secondValue < 86400)
      return (secondValue / 3600).toFixed(secondValue < 36000 ? 1 : 0) + 'h'
    if (secondValue < 864e5)
      return (secondValue / 86400).toFixed(secondValue < 864000 ? 1 : 0) + 'd'
    return (secondValue / 86400).toExponential(1) + 'd'
  } else {
    if (secondValue === 0) return '0.0s'
    if (1e-3 <= secondValue)
      return (secondValue * 1e3).toFixed(1e-2 <= secondValue ? 0 : 1) + 'ms'
    if (1e-6 <= secondValue)
      return (secondValue * 1e6).toFixed(1e-5 <= secondValue ? 0 : 1) + 'µs'
    return (secondValue * 1e9).toFixed(1e-8 <= secondValue ? 0 : 1) + 'ns'
  }
}

// 部署目标类型
interface DeploymentTarget {
  type: string
  clusterType?: string | null
  detailType?: string
  deployment?: {
    id: number
    type: string
    clusterType?: string | null
    instance: {
      name: string
    }
  }
  node?: {
    name: string
    cluster: { id: number }
    device: { name: string }
  }
}

/**
 * 创建报警事件部署目标转换函数
 *
 * @param type - 目标类型
 * @param deploymentType - 部署类型
 * @param clusterType - 集群类型
 * @returns 转换函数
 */
const createAlarmEventDeploymentTarget =
  (type: string, deploymentType: string, clusterType: string | null = null) =>
  (
    alarmEvent: ApiGetDomeosOverviewAlarmEventResponse[0]
  ): DeploymentTarget => ({
    type,
    clusterType,
    deployment: {
      id: alarmEvent.targetId,
      type: deploymentType,
      clusterType,
      instance: {
        name: alarmEvent.name
      }
    },
    detailType: deploymentType
  })

/**
 * 转换报警操作符
 *
 * @param operator - 操作符类型
 * @returns 转换后的操作符
 */
const transAlarmOperator = (operator: string): string => {
  const operatorMap: Record<string, string> = {
    equal: 'EQ',
    unequal: 'NE',
    greater: 'GT',
    equal_greater: 'GE',
    less: 'LT',
    equal_less: 'LE'
  }
  return operatorMap[operator] || operator
}

/**
 * 报警值格式化过滤器
 * 根据指标类型格式化报警值
 *
 * @param value - 数值
 * @param index - 指标索引
 * @returns 格式化后的值
 */
const alarmValueFilter = (value: number | string, index: string): string => {
  if (['FSREAD', 'FSWRITE', 'NETTRANS', 'NETRECV'].includes(index)) {
    return transByte(Number(value)).replace('B', 'B/s')
  } else if (
    [
      'CPUPER',
      'CPULIMITPER',
      'CPUREQUESTPER',
      'MEMPER',
      'FSUSEDPER',
      'FSBUSY'
    ].includes(index)
  ) {
    return transPercent(Number(value))
  } else if (
    [
      'CPUUSAGE',
      'CPULOAD',
      'CPULOAD1MIN',
      'CPULOAD5MIN',
      'CPULOAD15MIN',
      'CPUCFS'
    ].includes(index)
  ) {
    const numValue = Number(value)
    if (Number.isInteger(numValue)) return numValue.toFixed(0)
    return numValue.toFixed(2)
  } else if (['FSREADTIME', 'FSWRITETIME'].includes(index)) {
    return transSecond(Number(value))
  } else {
    return String(value)
  }
}

/**
 * 转换指标索引
 * 将业务指标名称转换为系统指标索引
 *
 * @param index - 业务指标名称
 * @param type - 目标类型（Node 或 Deployment）
 * @returns 系统指标索引
 */
const transNumberStrategyIndex = (index: string, type: string): string => {
  const indexMap: Record<string, string> = {
    cpu_percent: type === 'Node' ? 'CPUPER' : 'CPULIMITPER',
    cpu_request_percent: 'CPUREQUESTPER',
    cpu_usage: 'CPUUSAGE',
    cpu_load_1min: 'CPULOAD1MIN',
    cpu_load_5min: 'CPULOAD5MIN',
    cpu_load_15min: 'CPULOAD15MIN',
    memory_percent: 'MEMPER',
    disk_percent: 'FSUSEDPER',
    disk_read: 'FSREAD',
    disk_write: 'FSWRITE',
    disk_use: 'FSUSED ',
    network_in: 'NETRECV',
    network_out: 'NETTRANS',
    disk_io_used_per_sec: 'FSBUSY',
    disk_io_read_consumer: 'FSREADTIME',
    disk_io_write_consumer: 'FSWRITETIME',
    cpu_throttled_percent: 'CPUCFS'
  }
  return indexMap[index] || index
}

/**
 * 报警事件数据转换函数
 * 将 API 返回的报警事件数据转换为前端使用的格式
 *
 * @param alarmEvent - 原始报警事件数据
 * @returns 转换后的报警事件数据
 */
export function domeosTransformAlertEvent(
  alarmEvent: ApiGetDomeosOverviewAlarmEventResponse[0]
) {
  // 目标类型映射
  const targetTypeMap: Record<
    string,
    (alarmEvent: ApiGetDomeosOverviewAlarmEventResponse[0]) => any
  > = {
    deploy: createAlarmEventDeploymentTarget('Deployment', 'Normal'),
    cron: createAlarmEventDeploymentTarget('Deployment', 'Schedule'),
    loadbalancer: createAlarmEventDeploymentTarget('Nginx', 'Nginx'),
    cluster_dns: createAlarmEventDeploymentTarget(
      'ClusterComponent',
      'ClusterComponent',
      'Dns'
    ),
    cluster_watcher: createAlarmEventDeploymentTarget(
      'ClusterComponent',
      'ClusterComponent',
      'Watcher'
    ),
    cluster_agent: createAlarmEventDeploymentTarget(
      'ClusterComponent',
      'ClusterComponent',
      'Agent'
    ),
    cluster_business_prom: createAlarmEventDeploymentTarget(
      'ClusterComponent',
      'ClusterComponent',
      'BusinessPrometheus'
    ),
    host: (alarmEvent: ApiGetDomeosOverviewAlarmEventResponse[0]) => ({
      type: 'Node',
      node: {
        name: alarmEvent.name,
        cluster: { id: alarmEvent.targetId },
        device: { name: alarmEvent.tag }
      }
    })
  }

  return {
    id: alarmEvent.id,
    silent: { active: false, suppressed: true }[alarmEvent.state] ?? false,
    createTime: new Date(alarmEvent.createTime),
    lastUpdateTime: formatTime(alarmEvent.lastAlarmTime),
    alarm: {
      id: alarmEvent.templateId,
      name: alarmEvent.templateName,
      target: targetTypeMap[alarmEvent.alarmType]?.(alarmEvent)
    },
    collection: {
      id: alarmEvent.alarmCollectionId || alarmEvent.alarmCollection?.id,
      name: alarmEvent.alarmCollection?.name
    },
    rule: {
      id: alarmEvent.strategyId,
      index:
        alarmIndexFilter[
          transNumberStrategyIndex(
            alarmEvent.metric,
            alarmEvent.alarmType === 'host' ? 'Node' : 'Deployment'
          )
        ],
      result: alarmEvent.alarmNumber,
      operator: { EQ: '=', NE: '≠', GT: '>', GE: '≥', LT: '<', LE: '≤' }[
        transAlarmOperator(alarmEvent.operatorType)
      ],
      value: alarmValueFilter(
        alarmEvent.rightValue,
        transNumberStrategyIndex(
          alarmEvent.metric,
          alarmEvent.alarmType === 'host' ? 'Node' : 'Deployment'
        )
      )
    },
    role: alarmEvent.role,
    may: {
      silent: ['OWNER', 'MASTER', 'DEVELOPER'].includes(alarmEvent.role)
    }
  }
}
